<script setup lang="ts">
  import { computed, onMounted, PropType, ref, watch } from 'vue'

  import { Modal } from 'bootstrap'

  const MODAL_ID = 'modal-email-ownership-otp'
  const OTP_LENGTH = 6

  const modal = ref<InstanceType<typeof Modal> | null>(null)
  const digits = ref<string[]>(Array(OTP_LENGTH).fill(''))
  const inputRefs = ref<(HTMLInputElement | null)[]>([])

  const props = defineProps({
    show: {
      type: Boolean,
      required: false,
      default: false,
    },
    email: {
      type: String,
      required: true,
    },
    otpPrefix: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function as PropType<() => void>,
      required: false,
      default: () => void 0,
    },
  })

  const emit = defineEmits<{
    (event: 'submit', otp: string): void
    (event: 'resend'): void
    (event: 'change-email'): void
  }>()

  const otp = computed(() => digits.value.join(''))
  const isComplete = computed(() => digits.value.every((digit) => digit !== ''))

  onMounted(() => {
    modal.value = new Modal(`#${MODAL_ID}`, {
      backdrop: 'static',
      keyboard: false,
    })
  })

  watch(
    () => props.show,
    (newValue) => {
      if (newValue) {
        digits.value = Array(OTP_LENGTH).fill('')
        modal.value?.show()
        return
      }

      // Fecha programaticamente (ex.: sucesso na validação) sem esperar a
      // transição do Bootstrap, evitando um backdrop órfão ao navegar em seguida
      forceHide()
    },
  )

  function forceHide() {
    modal.value?.hide()
    document
      .querySelectorAll('.modal-backdrop')
      .forEach((backdrop) => backdrop.remove())
    document.getElementById(MODAL_ID)?.classList.remove('show')
    document.body.classList.remove('modal-open')
  }

  function setInputRef(el: unknown, index: number) {
    inputRefs.value[index] = el as HTMLInputElement | null
  }

  function handleInput(index: number, event: Event) {
    const target = event.target as HTMLInputElement
    const value = target.value.replace(/\D/g, '').slice(-1)
    digits.value[index] = value
    target.value = value

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.value[index + 1]?.focus()
    }
  }

  function handleKeydown(index: number, event: KeyboardEvent) {
    if (event.key === 'Backspace' && !digits.value[index] && index > 0) {
      inputRefs.value[index - 1]?.focus()
    }
  }

  function handlePaste(event: ClipboardEvent) {
    event.preventDefault()

    const pastedDigits = (event.clipboardData?.getData('text') ?? '')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH)

    if (!pastedDigits) {
      return
    }

    digits.value = Array.from(
      { length: OTP_LENGTH },
      (_, index) => pastedDigits[index] ?? '',
    )

    const nextIndex = Math.min(pastedDigits.length, OTP_LENGTH - 1)
    inputRefs.value[nextIndex]?.focus()
  }

  function submit() {
    if (!isComplete.value || props.loading) {
      return
    }
    emit('submit', otp.value)
  }

  function hide() {
    modal.value?.hide()
    setTimeout(() => {
      props.onClose?.()
    }, 300)
  }

  function changeEmail() {
    emit('change-email')
    hide()
  }

  function resend() {
    emit('resend')
  }
</script>

<template>
  <div
    :id="MODAL_ID"
    class="modal fade"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
    aria-labelledby="modalEmailOwnershipOtpTitle"
  >
    <div class="modal-dialog text-start">
      <div class="modal-content">
        <div class="modal-header">
          <h1 id="modalEmailOwnershipOtpTitle" class="modal-title fs-5">
            Confirme seu e-mail
          </h1>
          <button
            class="btn-close"
            type="button"
            aria-label="Fechar"
            @click="hide"
          ></button>
        </div>
        <div class="modal-body">
          <p>
            Enviamos um código de 6 dígitos para
            <span class="fw-600">{{ email }}</span
            >. Digite o código abaixo para confirmar sua titularidade.
          </p>

          <div class="otp-wrapper d-flex align-items-center gap-2">
            <span class="otp-prefix">{{ otpPrefix }}</span>
            <span>-</span>
            <input
              v-for="index in OTP_LENGTH"
              :key="index"
              :ref="(el) => setInputRef(el, index - 1)"
              class="otp-digit form-control text-center"
              type="text"
              inputmode="numeric"
              maxlength="1"
              :value="digits[index - 1]"
              @input="handleInput(index - 1, $event)"
              @keydown="handleKeydown(index - 1, $event)"
              @paste="handlePaste($event)"
            />
          </div>

          <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
            {{ errorMessage }}
          </div>
        </div>
        <div class="modal-footer flex-column align-items-stretch border-top-0">
          <button
            class="btn btn-primary w-100"
            type="button"
            :disabled="!isComplete || loading"
            @click="submit"
          >
            <span v-if="!loading">Confirmar código</span>
            <div
              v-else
              class="spinner-border spinner-border-sm"
              role="status"
            ></div>
          </button>
          <p class="text-center small mt-2 mb-0">
            Não recebeu o código ou deseja
            <button type="button" class="link-btn" @click="resend">
              reenviar
            </button>
            ou
            <button type="button" class="link-btn" @click="changeEmail">
              trocar o e-mail</button
            >?
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="stylus" scoped>
  .modal-dialog
    max-width: 460px

  .otp-wrapper
    margin-top: 1rem

  .otp-prefix
    display: inline-flex
    align-items: center
    justify-content: center
    min-width: 2.5rem
    height: 2.5rem
    background-color: #F0F0F0
    border-radius: 0.375rem
    font-weight: 600

  .otp-digit
    width: 2.5rem
    height: 2.5rem
    padding: 0

  .link-btn
    background: none
    border: none
    padding: 0
    color: var(--bs-primary, #0d6efd)
    text-decoration: underline

    &:disabled
      color: inherit
      text-decoration: none
      cursor: default

  @media screen and (max-width: 576px)
    .modal-dialog
      height: auto
      margin: 0.5rem
    .modal-content
      border-radius: 0.5rem
</style>
